import {Vector2} from "three";
import {VectorHelper} from "features/apartmentViewer/ApartmentViewer/helpers/VectorHelper";
import {CameraHandler} from "features/apartmentViewer/ApartmentViewer/services/CameraHandler";
import {CanvasService} from "features/apartmentViewer/ApartmentViewer/services/CanvasService";

export class EventManager {

    dragScale = new Vector2(1, 1);

    mouseButtonsState: Map<number, boolean> = new Map();
    mouseButtonsDown: Map<number, boolean> = new Map();
    mouseDownPosition: Vector2 = new Vector2();
    mouseButtonsUp:  Map<number, boolean> = new Map();
    mouseUpPosition: Vector2 = new Vector2();
    mouseScrollDelta = new Vector2(0, 0);
    mousePosition = new Vector2();
    mouseDragDelta = new Vector2();

    keysState = new Map<string, boolean>();
    keysDown = new Map<string, boolean>();
    keysUp = new Map<string, boolean>();

    mouseDownTime: number = 0;
    mouseUpTime: number = 0;
    maxTapTime: number = 200;
    maxTapMove: number = 5;
    lastTouchPosition = new Vector2();
    touchZoomDistanceStart : number = 0;
    touchZoomDistanceEnd : number = 0;

    constructor(
        private canvasService: CanvasService,
        private cameraHandler: CameraHandler
    ) {
        this.addEventListeners();
    }


    addEventListeners() {
        document.addEventListener("mouseup", this.handleMouseUp.bind(this));
        document.addEventListener("keydown", this.handleKeyDown.bind(this));
        document.addEventListener("keyup", this.handleKeyUp.bind(this));

        this.canvasService.getCanvas().addEventListener("mousedown", this.handleMouseDown.bind(this));
        this.canvasService.getCanvas().addEventListener("mousemove", this.handleMouseMove.bind(this));

        this.canvasService.getCanvas().addEventListener("touchstart", this.handleTouchStart.bind(this));
        this.canvasService.getCanvas().addEventListener("touchend", this.handleTouchEnd.bind(this));
        this.canvasService.getCanvas().addEventListener("touchmove", this.handleTouchMove.bind(this));
        this.canvasService.getCanvas().addEventListener("wheel", this.handleMouseWheel.bind(this));
    }

    handleKeyDown(event: KeyboardEvent): void {
        if (this.canvasService.parentElement && this.canvasService.parentElement.matches(":hover")) {
            event.preventDefault();
            this.keysState.set(event.code, true);
            this.keysDown.set(event.code, true);
        }
    }

    handleKeyUp(event: KeyboardEvent): void {
        if (this.canvasService.parentElement && this.canvasService.parentElement.matches(":hover")) {
            event.preventDefault();
            this.keysState.set(event.code, false);
            this.keysUp.set(event.code, true);
        }
    }

    handleMouseDown(event: MouseEvent) {
        this.handleInputDown(event.button, event.clientX, event.clientY)
    }

    handleMouseUp(event: MouseEvent): void {
        this.handleInputUp(event.button, event.clientX, event.clientY);
    }

    handleMouseMove(event: MouseEvent) {
        this.handleInputMove(event.clientX, event.clientY, event.movementX, event.movementY)
    }

    handleTouchStart(event: TouchEvent) {
        if (event.touches.length === 1) {
            this.handleInputDown(0, event.touches[0].clientX, event.touches[0].clientY);
            this.lastTouchPosition.set(event.touches[0].clientX, event.touches[0].clientY);
        } else if (event.touches.length === 2) {
            const dx = event.touches[0].clientX - event.touches[1].clientX;
            const dy = event.touches[0].clientY - event.touches[1].clientY;

            this.touchZoomDistanceStart = Math.hypot(dx, dy);
            this.touchZoomDistanceEnd = this.touchZoomDistanceStart;
        }
    }

    handleTouchEnd(event: TouchEvent): void {
        const touch = event.changedTouches[0];
        this.handleInputUp(0, touch.clientX, touch.clientY);

        // Reset zoom distances after touch ends
        this.touchZoomDistanceStart = 0;
        this.touchZoomDistanceEnd = 0;
    }

    handleTouchMove(event: TouchEvent): void {
        if (event.touches.length === 1) {
            const touch = event.touches[0];
            const dx = touch.clientX - this.lastTouchPosition.x;
            const dy = touch.clientY - this.lastTouchPosition.y;
            this.handleInputMove(touch.clientX, touch.clientY, dx, dy);
            this.lastTouchPosition.set(touch.clientX, touch.clientY);
        } else if (event.touches.length === 2) {
            const dx = event.touches[0].clientX - event.touches[1].clientX;
            const dy = event.touches[0].clientY - event.touches[1].clientY;
            this.touchZoomDistanceEnd = Math.hypot(dx, dy);

            const zoomDelta = this.touchZoomDistanceEnd - this.touchZoomDistanceStart;
            this.touchZoomDistanceStart = this.touchZoomDistanceEnd;
            this.mouseScrollDelta.set(0, -2 * zoomDelta);
        }
    }

    handleMouseWheel(event: WheelEvent): void {
        this.mouseScrollDelta.set(event.deltaX, event.deltaY);
    }

    handleInputUp(button: number, clientX: number, clientY: number): void {
        this.mouseButtonsState.set(button, false);
        this.mouseButtonsUp.set(button, true);
        this.mouseUpPosition.set(clientX, clientY);
        this.mouseUpTime = performance.now();
    }

    handleInputDown(button: number, clientX: number, clientY: number) {
        this.mouseButtonsState.set(button, true);
        this.mouseButtonsDown.set(button, true);
        this.mouseDownPosition.set(clientX, clientY);
        this.mouseDownTime = performance.now();
    }

    handleInputMove(t: number, e: number, n: number, i: number) {
        this.mousePosition.set(t, e);
        this.mouseDragDelta.x += n * this.dragScale.x;
        this.mouseDragDelta.y += i * this.dragScale.y;
    }

    GetMouseButtonDown(button: number): boolean {
        return this.mouseButtonsState.get(button) === true;
    }

    GetMouseButton(t: number) {
        return this.mouseButtonsState.get(t) || false
    }

    get mousePositionNormalized() {
        const t = this.canvasService.getCanvas().getBoundingClientRect();
        const e = new Vector2(this.mousePosition.x - t.left, this.mousePosition.y - t.top);

        return new Vector2(e.x / t.width * 2 - 1,-e.y / t.height * 2 + 1)
    }

    GetMouseButtonUp(button: number): boolean {
        return !!this.mouseButtonsUp.get(button);
    }

    get MousePick(): boolean {
        if (this.GetMouseButtonUp(0)) { // Check if the left mouse button (0) was released.
            const moveDistance = this.mouseDownPosition.distanceTo(this.mouseUpPosition); // Calculate distance between down and up positions.
            const pressDuration = this.mouseUpTime - this.mouseDownTime; // Calculate how long the mouse button was held down.

            // Return true if both movement and press duration are within the allowed thresholds.
            return moveDistance <= this.maxTapMove && pressDuration <= this.maxTapTime;
        }
        return false;
    }

    public KeyUp(key:string): boolean {
        return !!this.keysUp.get(key);
    }

    public KeyDown(key:string): boolean {
        return !!this.keysDown.get(key);
    }

    endFrame(): void {
        this.mouseButtonsUp.set(0, false);
        this.mouseButtonsUp.set(1, false);
        this.mouseButtonsDown.set(0, false);
        this.mouseButtonsDown.set(1, false);
        this.keysUp.clear();
        this.keysDown.clear();
        this.mouseDragDelta.set(0, 0);
        this.mouseScrollDelta.set(0, 0);
    }

    update() {


        if (this.GetMouseButton(0)) {
            const mouseSensitivity = this.cameraHandler.targetFov / 300;

            // Adjust camera pitch (rotation.x) using mouse delta
            this.cameraHandler.targetRotation.x += this.mouseDragDelta.y * mouseSensitivity * VectorHelper.DegToRad;

            // Clamp camera pitch rotation within allowed bounds
            this.cameraHandler.targetRotation.x = Math.max(
                -60 * VectorHelper.DegToRad,
                Math.min(60 * VectorHelper.DegToRad, this.cameraHandler.targetRotation.x)
            );

            // Adjust camera yaw (rotation.y) using mouse delta
            this.cameraHandler.targetRotation.y += this.mouseDragDelta.x * mouseSensitivity * 0.8 * VectorHelper.DegToRad;
        }

        if (this.mouseScrollDelta.y !== 0) {
            this.cameraHandler.targetFov += 0.1 * this.mouseScrollDelta.y;
            this.cameraHandler.targetFov = Math.max(40, Math.min(100, this.cameraHandler.targetFov));
        }

    }
}
